import { useLayoutEffect, useRef, type RefObject } from "react";
import { useSearchParams } from "react-router-dom";
import { useCourseDetailStore } from "@/stores/courseDetailStore";
import { CourseMarkdown } from "@/components/storyboard/CourseMarkdown";
import StoryboardList from "@/components/storyboard/StoryboardList";
import { GlossaryHighlightProvider } from "@/components/glossary/GlossaryHighlightContext";
import PageHeadline from "@/components/course/PageHeadline";
import ScrollToContentButton from "@/components/course/ScrollToContentButton";
import { SubchapterNavigation } from "@/components/course/SubchapterNavigation";
import { objectivesListMapping } from "@/components/course/ObjectivesListMapping";
import {
  REVEAL_SCROLL_TOP_PX,
  useChapterIntroReveal,
} from "@/hooks/course/useChapterIntroReveal";
import { useFirstSubChapterId } from "@/hooks/course/useFirstSubChapterId";
import { useSyncIntroTocFocus } from "@/hooks/course/useSyncIntroTocFocus";
import { isUnveilQuery } from "@/lib/chapterNavigation";
import { contentEnterClass } from "@/lib/contentEnterAnimation";
import { cn } from "@/lib/utils";
import type { CourseTreeNode } from "@/types/generated";
import type { Storyboard } from "@/types/storyboard";
import type { GlossaryTerm } from "@/types/glossary";

interface ChapterIntroScrollProps {
  title: string;
  body: string;
  contentTitle?: string;
  contentElements: CourseTreeNode[];
  storyboards: Storyboard[];
  storyboardsLoading: boolean;
  glossaryTerms?: GlossaryTerm[];
  onContentSeen?: () => void;
  illustrationSrc: string | null;
  className?: string;
}

interface IntroHeroIllustrationProps {
  src: string;
}

/**
 * Floating intro artwork — used beside the chapter title and objectives.
 */
const IntroHeroIllustration = ({ src }: IntroHeroIllustrationProps) => (
  <div
    aria-hidden="true"
    className="relative float-right mb-4 ml-6 w-48 shrink-0 sm:w-56 md:w-64 lg:mb-6 lg:ml-10 lg:w-72 xl:w-80"
  >
    <div className="absolute -inset-6 -z-10 rounded-full bg-linear-to-br from-primary/12 to-secondary/18 blur-2xl" />
    <img src={src} alt="" className="relative w-full object-contain" />
  </div>
);

/** First lesson element the learner has not finished, if any remain. */
const firstIncompleteElementId = (
  elementIds: readonly string[],
  completedIds: ReadonlySet<string>
): string | null =>
  elementIds.find((elementId) => !completedIds.has(elementId)) ?? null;

const lessonScrollOptions = { behavior: "auto", block: "start" } as const;

/**
 * Scrolls the unveiled lesson into view. A short chapter introduction is
 * shorter than the lesson block's scroll margin, which would leave the page
 * at the top and veil the lesson again — then the lesson band is scrolled
 * instead. A scrollport with no layout (jsdom) keeps the lesson-block target.
 */
const scrollUnveiledLessonIntoView = (
  resumeTarget: HTMLElement,
  lessonBand: HTMLElement | null
) => {
  const scrollRoot = resumeTarget.closest(".overflow-y-auto");
  if (!(scrollRoot instanceof HTMLElement)) {
    resumeTarget.scrollIntoView(lessonScrollOptions);
    return;
  }

  const scrollportHasNoLayout = scrollRoot.clientHeight === 0;
  if (scrollportHasNoLayout || !lessonBand) {
    resumeTarget.scrollIntoView(lessonScrollOptions);
    return;
  }

  const targetTop =
    resumeTarget.getBoundingClientRect().top -
    scrollRoot.getBoundingClientRect().top +
    scrollRoot.scrollTop;
  const scrollMarginTop =
    parseFloat(getComputedStyle(resumeTarget).scrollMarginTop) || 0;
  const marginLeavesLessonVeiled =
    targetTop - scrollMarginTop <= REVEAL_SCROLL_TOP_PX;

  if (marginLeavesLessonVeiled) {
    lessonBand.scrollIntoView(lessonScrollOptions);
    return;
  }

  resumeTarget.scrollIntoView(lessonScrollOptions);
};

const renderIntroHero = (src: string | null) => {
  if (!src) return null;
  return <IntroHeroIllustration src={src} />;
};

interface ChapterIntroProps {
  title: string;
  body: string;
  illustrationSrc: string | null;
  showScrollCue: boolean;
  isScrollCueAnimated: boolean;
  onScrollCueClick: () => void;
}

/**
 * Chapter intro band — title, objectives markdown, and optional scroll cue.
 */
const ChapterIntro = ({
  title,
  body,
  illustrationSrc,
  showScrollCue,
  isScrollCueAnimated,
  onScrollCueClick,
}: ChapterIntroProps) => (
  <div className="shrink-0 bg-background px-10 pt-8 pb-1">
    <div className="max-w-5xl">
      {renderIntroHero(illustrationSrc)}
      <PageHeadline title={title} level="h1" className="mb-8" />

      <div className="prose max-w-full text-h4 leading-body marker:text-primary prose-strong:text-primary">
        <CourseMarkdown components={objectivesListMapping}>
          {body}
        </CourseMarkdown>
      </div>
      <div className="clear-both" />
    </div>

    {showScrollCue && (
      <ScrollToContentButton
        isAnimated={isScrollCueAnimated}
        onClick={onScrollCueClick}
      />
    )}
  </div>
);

interface ChapterContentProps {
  contentRef: RefObject<HTMLDivElement | null>;
  contentRevealed: boolean;
  contentTitle?: string;
  contentElements: CourseTreeNode[];
  storyboards: Storyboard[];
  storyboardsLoading: boolean;
  glossaryTerms: GlossaryTerm[];
}

/**
 * First-subchapter content band — blurred until unveiled by scroll/chevron.
 */
const ChapterContent = ({
  contentRef,
  contentRevealed,
  contentTitle,
  contentElements,
  storyboards,
  storyboardsLoading,
  glossaryTerms,
}: ChapterContentProps) => {
  const firstSubchapterId = useFirstSubChapterId();

  return (
    <div
      ref={contentRef}
      className={cn(
        "relative flex flex-1 flex-col bg-background px-10 pt-8 pb-16 transition-[filter,opacity] duration-500",
        !contentRevealed &&
          "pointer-events-none opacity-70 blur-[3px] saturate-75"
      )}
      inert={!contentRevealed ? true : undefined}
    >
      {contentTitle && (
        <PageHeadline
          title={contentTitle}
          level="h2"
          headingClassName="text-heading-h1"
          className="mb-8"
        />
      )}

      <GlossaryHighlightProvider
        terms={glossaryTerms}
        resetKey={contentTitle ?? "intro-content"}
      >
        <StoryboardList
          loading={storyboardsLoading}
          storyboards={storyboards}
          selectedContentElements={contentElements}
        />
      </GlossaryHighlightProvider>

      <SubchapterNavigation subchapterId={firstSubchapterId} />

      {!contentRevealed && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-linear-to-b from-background to-transparent"
        />
      )}
    </div>
  );
};

/**
 * Chapter intro: white intro with scroll cue, then first-subchapter content
 * that unveils on scroll.
 */
const ChapterIntroScroll = ({
  title,
  body,
  contentTitle,
  contentElements,
  storyboards,
  storyboardsLoading,
  glossaryTerms = [],
  onContentSeen,
  illustrationSrc,
  className,
}: ChapterIntroScrollProps) => {
  const [searchParams] = useSearchParams();
  const unveilQuery = isUnveilQuery(searchParams);
  const completedContentElementIds = useCourseDetailStore(
    (state) => state.completedContentElementIds
  );
  const { rootRef, contentRef, contentRevealed, scrollContentIntoView } =
    useChapterIntroReveal(onContentSeen, unveilQuery);
  useSyncIntroTocFocus(contentRevealed);
  const unveiledScrollDone = useRef(false);

  const scrollFirstUnfinishedElementIntoView = () => {
    if (!unveilQuery) {
      unveiledScrollDone.current = false;
      return;
    }
    if (storyboardsLoading || unveiledScrollDone.current) return;

    const incompleteId = firstIncompleteElementId(
      contentElements.map((element) => element.id),
      completedContentElementIds
    );
    const resumeTarget = incompleteId
      ? document.getElementById(`element-${incompleteId}`)
      : contentRef.current;
    if (!resumeTarget) return;

    unveiledScrollDone.current = true;
    scrollUnveiledLessonIntoView(resumeTarget, contentRef.current);
  };
  useLayoutEffect(scrollFirstUnfinishedElementIntoView, [
    unveilQuery,
    storyboardsLoading,
    contentElements,
    completedContentElementIds,
    contentRef,
  ]);

  const hasContent =
    storyboardsLoading || contentElements.length > 0 || storyboards.length > 0;

  return (
    <div
      ref={rootRef}
      className={cn("flex min-h-full flex-col", contentEnterClass, className)}
    >
      <ChapterIntro
        title={title}
        body={body}
        illustrationSrc={illustrationSrc}
        showScrollCue={hasContent}
        isScrollCueAnimated={!contentRevealed}
        onScrollCueClick={scrollContentIntoView}
      />

      {hasContent && (
        <ChapterContent
          contentRef={contentRef}
          contentRevealed={contentRevealed}
          contentTitle={contentTitle}
          contentElements={contentElements}
          storyboards={storyboards}
          storyboardsLoading={storyboardsLoading}
          glossaryTerms={glossaryTerms}
        />
      )}
    </div>
  );
};

export default ChapterIntroScroll;
